/**
 * Comms enums (KTD5). Each Zod enum mirrors the Prisma enum of the same name
 * in `apps/backend/prisma/schema/comms-*.prisma` (KTD3), value for value.
 */
import { z } from 'zod'

export const CommsChannelSchema = z.enum(['EMAIL', 'PUSH', 'SMS'])
export type CommsChannel = z.infer<typeof CommsChannelSchema>

export const CommsCategorySchema = z.enum(['EMERGENCY', 'OPERATIONAL', 'NEWSLETTER', 'MARKETING'])
export type CommsCategory = z.infer<typeof CommsCategorySchema>

export const CommsDeliveryStatusSchema = z.enum([
  'PENDING',
  'HELD',
  'QUEUED',
  'SENDING',
  'UNKNOWN',
  'SENT',
  'DEFERRED',
  'DELIVERED',
  'BOUNCED',
  'SUPPRESSED',
  'CANCELLED',
  'REJECTED',
  'FAILED'
])
export type CommsDeliveryStatus = z.infer<typeof CommsDeliveryStatusSchema>

export const CommsSkipReasonSchema = z.enum([
  'UNSUBSCRIBED_TOPIC',
  'UNSUBSCRIBED_ALL',
  'NOT_OPTED_IN',
  'ADDRESS_HARD_BOUNCED',
  'ADDRESS_COMPLAINED',
  'ADDRESS_PROVIDER_SUPPRESSED',
  'SOFT_BOUNCE_THRESHOLD',
  'MANUAL',
  'INVALID_ADDRESS',
  'ROLE_ADDRESS',
  'DUPLICATE_ADDRESS',
  'HOUSEHOLD_DEDUPE',
  'NO_CONTACT_POINT',
  'STUDENT_EMAIL_OFF',
  'RESTRICTED_RELATIONSHIP',
  'INACTIVE_MEMBER',
  'FREQUENCY_CAP',
  'HOLD_EXPIRED',
  'FLOW_STOPPED'
])
export type CommsSkipReason = z.infer<typeof CommsSkipReasonSchema>

/**
 * Why a delivery is held. `FLOW_PAUSED` is plan 2's hold on a `FLOW_NODE`
 * delivery whose flow is paused; `NEEDS_CONFIRMATION` is a pause or
 * global-stop hold past its expiry, which sends only after the sender
 * confirms (R62).
 */
export const CommsHoldReasonSchema = z.enum([
  'QUIET_HOURS',
  'FREQUENCY_CAP',
  'AB_REMAINDER',
  'SENDING_PAUSED',
  'CAP_STORE_UNAVAILABLE',
  'FLOW_PAUSED',
  'NEEDS_CONFIRMATION'
])
export type CommsHoldReason = z.infer<typeof CommsHoldReasonSchema>

/**
 * What a send asks for (`responseRequest`, the `nonResponders` audience).
 * Stored `CommsResponse` rows are ACK and RSVP only (`CommsResponseKindSchema`).
 */
export const CommsResponseRequestKindSchema = z.enum(['ACK', 'RSVP', 'FORM'])
export type CommsResponseRequestKind = z.infer<typeof CommsResponseRequestKindSchema>

export const CommsSendKindSchema = z.enum(['BROADCAST', 'FLOW_NODE', 'TEST', 'TRANSACTIONAL'])
export type CommsSendKind = z.infer<typeof CommsSendKindSchema>

export const CommsSendStatusSchema = z.enum([
  'PLANNING',
  'MATERIALIZING',
  'READY',
  'SENDING',
  'PAUSED',
  'SETTLED',
  'CANCELLED'
])
export type CommsSendStatus = z.infer<typeof CommsSendStatusSchema>

export const CommsBroadcastStatusSchema = z.enum(['DRAFT', 'SCHEDULED', 'SENDING', 'PAUSED', 'SENT', 'CANCELLED'])
export type CommsBroadcastStatus = z.infer<typeof CommsBroadcastStatusSchema>

export const CommsConsentStateValueSchema = z.enum(['SUBSCRIBED', 'UNSUBSCRIBED'])
export type CommsConsentStateValue = z.infer<typeof CommsConsentStateValueSchema>

export const CommsConsentMethodSchema = z.enum([
  'ONE_CLICK',
  'PREFERENCE_CENTER',
  'ADMIN',
  'COMPLAINT',
  'IMPORT',
  'STOP',
  'FLOW'
])
export type CommsConsentMethod = z.infer<typeof CommsConsentMethodSchema>

export const CommsSuppressionReasonSchema = z.enum([
  'HARD_BOUNCE',
  'COMPLAINT',
  'SOFT_BOUNCE_THRESHOLD',
  'PROVIDER_POLICY',
  'MANUAL',
  'INVALID'
])
export type CommsSuppressionReason = z.infer<typeof CommsSuppressionReasonSchema>

export const CommsContactSourceSchema = z.enum(['BB_PRIMARY', 'BB_CC', 'LOGIN', 'APP_REGISTERED', 'CRM'])
export type CommsContactSource = z.infer<typeof CommsContactSourceSchema>

export const CommsContactStatusSchema = z.enum(['ACTIVE', 'INVALID', 'ROLE_ADDRESS', 'BOUNCED'])
export type CommsContactStatus = z.infer<typeof CommsContactStatusSchema>

export const CommsRecipientKindSchema = z.enum(['MEMBER', 'PROSPECT', 'TEST'])
export type CommsRecipientKind = z.infer<typeof CommsRecipientKindSchema>

export const CommsHouseholdModeSchema = z.enum(['PER_ADDRESS', 'PER_HOUSEHOLD'])
export type CommsHouseholdMode = z.infer<typeof CommsHouseholdModeSchema>

export const CommsSubjectKindSchema = z.enum(['RECIPIENT', 'STUDENT_UOS', 'APPLICANT'])
export type CommsSubjectKind = z.infer<typeof CommsSubjectKindSchema>

export const CommsPauseReasonSchema = z.enum([
  'MANUAL',
  'SES_TENANT_PAUSED',
  'AUTO_PAUSE_BOUNCE',
  'AUTO_PAUSE_COMPLAINT'
])
export type CommsPauseReason = z.infer<typeof CommsPauseReasonSchema>

export const CommsTemplateKindSchema = z.enum(['SYSTEM', 'SCHOOL', 'SAVED_SECTION', 'MESSAGE'])
export type CommsTemplateKind = z.infer<typeof CommsTemplateKindSchema>

export const CommsTranslationStatusSchema = z.enum(['NONE', 'DRAFT', 'APPROVED'])
export type CommsTranslationStatus = z.infer<typeof CommsTranslationStatusSchema>

export const CommsResponseKindSchema = z.enum(['ACK', 'RSVP'])
export type CommsResponseKind = z.infer<typeof CommsResponseKindSchema>

/**
 * The six comms capability keys (KTD8). U10 adds these same keys to
 * `CapabilityKeySchema` in `capability-key.ts`, so these values are a subset
 * of it.
 */
export const CommsCapabilityKeySchema = z.enum([
  'comms_send',
  'comms_publish_flow',
  'comms_manage_domains',
  'comms_manage_templates',
  'comms_view_analytics',
  'comms_preview_recipient'
])
export type CommsCapabilityKey = z.infer<typeof CommsCapabilityKeySchema>

/**
 * The KTD12 partition of `CommsSkipReason`. The audience resolver records
 * these as its `resolverExclusion`.
 */
export const RESOLVER_SKIP_REASONS = [
  'NO_CONTACT_POINT',
  'STUDENT_EMAIL_OFF',
  'RESTRICTED_RELATIONSHIP',
  'INACTIVE_MEMBER'
] as const satisfies readonly CommsSkipReason[]
export type CommsResolverSkipReason = (typeof RESOLVER_SKIP_REASONS)[number]

/** The reasons `decideEligibility` sets itself (KTD12). */
export const ELIGIBILITY_SKIP_REASONS = [
  'UNSUBSCRIBED_TOPIC',
  'UNSUBSCRIBED_ALL',
  'NOT_OPTED_IN',
  'ADDRESS_HARD_BOUNCED',
  'ADDRESS_COMPLAINED',
  'ADDRESS_PROVIDER_SUPPRESSED',
  'SOFT_BOUNCE_THRESHOLD',
  'MANUAL',
  'INVALID_ADDRESS',
  'ROLE_ADDRESS'
] as const satisfies readonly CommsSkipReason[]
export type CommsEligibilitySkipReason = (typeof ELIGIBILITY_SKIP_REASONS)[number]

// In neither list: DUPLICATE_ADDRESS and HOUSEHOLD_DEDUPE (set by `applyDedupe`), and FREQUENCY_CAP, HOLD_EXPIRED and FLOW_STOPPED (set by plan 2 at send time).
